import AddCardIcon from '@mui/icons-material/AddCard';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Button,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material';
import type { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import MuiAppBar from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
import type { CSSObject, Theme } from '@mui/material/styles';
import { styled, useTheme } from '@mui/material/styles';
import { getAllVendors } from 'api/adminHub';
import type { Vendor as VendorType } from 'api/types';
import AdminConsole from 'assets/images/admin-console.svg';
import AnalyticsDashboard from 'assets/images/analytics-dashboard.svg';
import Bot from 'assets/images/bot.svg';
import TimeWSIcon from 'assets/images/chronic.svg';
import CollapsedLogo from 'assets/images/collapsed-logo.svg';
import CreditCard from 'assets/images/creditcard.svg';
import Customer from 'assets/images/customer.svg';
import DriverWorkspace from 'assets/images/driver-workspace.svg';
import JobEstimation from 'assets/images/estimate-hub.svg';
import EstimateItemWorkspaceIcon from 'assets/images/estimate-item-workspace.svg';
import EstimateWorkspaceIcon from 'assets/images/estimate-workspace.svg';
import HelpIcon from 'assets/images/help.png';
import Job from 'assets/images/job.svg';
import LogoutIcon from 'assets/images/logout.svg';
import logo from 'assets/images/onexerp-logo-updated.svg';
import OrderAndLogistics from 'assets/images/order-and-logistic.svg';
import PaymentWorkspace from 'assets/images/payment-workspace.svg';
import People from 'assets/images/people.svg';
import ProcurementWorkspace from 'assets/images/procurement-workspace.svg';
import ProfileIcon from 'assets/images/profile-avatar.png';
import PunchFormIcon from 'assets/images/punch_clock.svg';
import ReceivingWorkspace from 'assets/images/receiving-workspace.svg';
import RequestWorkspace from 'assets/images/request-workspace.svg';
import TimeTrackingAndAttendence from 'assets/images/time-tracking-and-attendence.svg';
import TransportationWorkspace from 'assets/images/transportation-workspace.svg';
import Vehicle from 'assets/images/vehicle.svg';
import Vendor from 'assets/images/vendor.svg';
import CautionBar from 'components/CautionBar';
import ChatModal from 'components/ChatModal';
import { DarkModeToggleDrawer } from 'components/DarkModeToggle';
import config from 'config/index';
import { useDarkMode } from 'contexts/DarkModeContext';
import { format } from 'date-fns';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCommonStore } from 'store/index';
import { menus } from './menus';
import NestedNavItem from './NestedNavItem';
import SingleNavItem from './SingleNavItem';

const drawerWidth = 286;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,

  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.drawer + 1
  }
});

const closedMixin = (theme: Theme, isMobile: boolean): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: isMobile ? '0' : `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`
  }
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  isMobile: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile'
})<AppBarProps>(({ theme, open, isMobile }) => ({
  backgroundColor: '#f4f4f4',
  marginBottom: '50px',
  color: '#000',
  boxShadow: 'none',
  zIndex: theme.zIndex.drawer,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  width: open
    ? `calc(100% - ${drawerWidth}px)`
    : isMobile
    ? '100%'
    : `calc(100% - ${theme.spacing(7)})`,
  [theme.breakpoints.up('sm')]: {
    width: open
      ? `calc(100% - ${drawerWidth}px)`
      : isMobile
      ? '0'
      : `calc(100% - ${theme.spacing(8)})`
  },
  ...(open && {
    marginLeft: isMobile ? '0' : drawerWidth,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile'
})<{
  open?: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }) => ({
  backgroundColor: '#f4f4f4',
  textAlign: 'left',
  flexGrow: 1,
  padding: theme.spacing(3),
  width: isMobile ? '0' : `calc(100% - ${drawerWidth}px)`,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile'
})<{
  open?: boolean;
  isMobile: boolean;
}>(({ theme, open, isMobile }) => ({
  width: isMobile ? '100%' : drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  paddingBottom: '100px',
  ...(open
    ? {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': {
          ...openedMixin(theme),
          border: 'none',
          paddingBottom: '100px'
        }
      }
    : {
        ...closedMixin(theme, isMobile),
        '& .MuiDrawer-paper': { ...closedMixin(theme, isMobile), border: 'none' }
      })
}));

export const ListItemNavButton = styled(ListItemButton)(() => ({
  borderRadius: '10px',
  border: '2px solid #f4f4f4',
  margin: '8px',
  padding: '8px'
}));

export const NavListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: '30px'
}));

const logout = () => {
  const clientId = config?.awsClientId;
  const appUrl = `${window.location.protocol}//${window.location.host}/home`;
  const logoutUri = encodeURIComponent(appUrl);

  localStorage.clear();
  window.location.href = `${config.awsCognitoAuthUrl}logout?&state=STATE&response_type=code&client_id=${clientId}&logout_uri=${logoutUri}&redirect_uri=${logoutUri}`;
};

const Preference = ({ open, setChatModal }: { open: boolean; setChatModal: (res) => void }) => (
  <Box
    sx={{
      marginTop: '100px',
      position: { xs: 'static', md: 'absolute' },
      bottom: 0,
      left: 0,
      right: 0,
      background: '#fff',
      width: 'inherit'
    }}>
    <List
      disablePadding
      subheader={
        <ListSubheader
          sx={{ textAlign: 'left', display: open ? 'block' : 'none' }}
          component="div"
          id="nested-list-subheader">
          PREFERENCES
        </ListSubheader>
      }
      sx={{ display: 'block', position: 'reletive', bottom: 0 }}>
      <ListItemNavButton
        sx={{
          bgcolor: 'background.paper',
          ':hover': {
            bgcolor: '#f6dfe6',
            // Add this to change text color on hover in dark mode
            color: 'text.primary',
            '& .MuiListItemText-primary': {
              color: 'common.black' // Force black text on light background
            }
          }
        }}
        onClick={() => setChatModal(true)}>
        <NavListItemIcon sx={{ marginRight: '5px' }}>
          <img src={Bot} width="30px" />
        </NavListItemIcon>
        <ListItemText
          primary={'I know everything, test me'}
          sx={{
            display: open ? 'block' : 'none',
            color: 'text.primary'
          }}
        />
      </ListItemNavButton>
      <Link
        to="https://forms.monday.com/forms/12c54837fdf1be5def1a1ef2796720f2?r=use1"
        target="_blank">
        <ListItemNavButton>
          <NavListItemIcon sx={{ marginRight: '5px' }}>
            <img src={HelpIcon} width="30px" />
          </NavListItemIcon>
          <ListItemText primary={'Report an issue'} sx={{ display: open ? 'block' : 'none' }} />
        </ListItemNavButton>{' '}
      </Link>
      <Link
        to="https://cloud-fruit.atlassian.net/wiki/external/MDVhZTRlM2ZmYjRkNDRkMmJjMTJlN2YyNzNhN2Y0MzA"
        target="_blank">
        <ListItemNavButton>
          <NavListItemIcon>
            <NavListItemIcon sx={{ marginRight: '5px' }}>
              <img src={HelpIcon} width="30px" />
            </NavListItemIcon>
          </NavListItemIcon>
          <ListItemText primary={'Help'} sx={{ display: open ? 'block' : 'none' }} />
        </ListItemNavButton>
      </Link>
      {/* <Box
        sx={{
          display: open ? 'block' : 'none',
          borderRadius: '8px',
          border: '2px solid #f4f4f4',
          margin: '7px'
        }}>
        <DarkModeToggleDrawer />
      </Box> */}
      <ListItemNavButton onClick={logout}>
        <NavListItemIcon sx={{ marginRight: '5px' }}>
          <img src={LogoutIcon} width="30px" />
        </NavListItemIcon>
        <ListItemText primary={'Log out'} sx={{ display: open ? 'block' : 'none' }} />
      </ListItemNavButton>
    </List>
  </Box>
);

export const renderIcon = (name: string) => {
  let icon = '';
  let width = '30px';
  if (name === 'order-and-logistic') {
    icon = OrderAndLogistics;
  } else if (name === 'request-workspace') {
    icon = RequestWorkspace;
  } else if (name === 'procurement-workspace') {
    icon = ProcurementWorkspace;
  } else if (name === 'ocr-po-workspace') {
    icon = ProcurementWorkspace;
  } else if (name === 'receiving-workspace') {
    icon = ReceivingWorkspace;
  } else if (name === 'po-history') {
    icon = ProcurementWorkspace;
  } else if (name === 'payment-workspace') {
    icon = PaymentWorkspace;
  } else if (name === 'transportation-workspace') {
    icon = TransportationWorkspace;
  } else if (name === 'driver-workspace') {
    icon = DriverWorkspace;
  } else if (name === 'time-tracking') {
    icon = TimeTrackingAndAttendence;
  } else if (name === 'job-estimation') {
    icon = JobEstimation;
  } else if (name === 'admin-console') {
    icon = AdminConsole;
  } else if (name === 'analytics') {
    icon = AnalyticsDashboard;
  } else if (name === 'people-workspace') {
    icon = People;
  } else if (name === 'vendor-workspace') {
    icon = Vendor;
  } else if (name === 'creditcard-workspace') {
    icon = CreditCard;
  } else if (name === 'customer-workspace') {
    icon = Customer;
  } else if (name === 'integration-workspace') {
    icon = Customer;
  } else if (name === 'job-workspace') {
    icon = Job;
  } else if (name === 'vehicle-workspace') {
    icon = Vehicle;
  } else if (name === 'punch-form') {
    icon = PunchFormIcon;
    width = '20px';
  } else if (name === 'time-workspace') {
    icon = TimeWSIcon;
    width = '20px';
  } else if (name === 'estimate-workspace') {
    icon = EstimateWorkspaceIcon;
  } else if (name === 'estimate-item-workspace') {
    icon = EstimateItemWorkspaceIcon;
  }

  return <img src={icon} width={width} style={{ marginRight: '10px' }} />;
};

interface Props {
  children: ReactNode;
}

const DashboardLayout: React.FC<Props> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(isMobile ? false : true);
  const [chatModal, setChatModal] = React.useState<boolean>(false);
  const navigate = useNavigate();

  const routeChange = () => {
    console.log('onClick={onClick}');

    const path = `admin-hub/integration-workspace`;
    navigate(path);
  };

  const getLocalStorageItem = (key) => {
    const value = localStorage.getItem(key);
    return value && value !== 'null' ? value : '';
  };

  const preferredName = getLocalStorageItem('preferredName');
  const firstName = getLocalStorageItem('firstName');
  const lastName = getLocalStorageItem('lastName');
  const laborCategory = getLocalStorageItem('laborCategory');
  const userName = preferredName ? preferredName : `${firstName} ${lastName}`.trim() || 'User';
  const userRole: string = localStorage.getItem('userRoles') ?? '';
  const componentHeading = useCommonStore((state) => state.componentHeading);
  const [activeInactive, setActiveInactive] = useState<{
    active: string;
    inactive: string;
  }>({
    active: '0',
    inactive: '0'
  });

  useEffect(() => {
    setOpen(isMobile ? false : true);
  }, [isMobile]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const getData = () => {
    getAllVendors('', '', '', '', false, false)
      .then((res: { data: VendorType[]; activeVendors: string; inactiveVendors: string }) => {
        setActiveInactive({
          active: res.activeVendors,
          inactive: String(res.data.length)
        });
      })
      .catch((err) => {
        console.log('err', err);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  const isProdEnv = window.location.host === 'onexerp.com';

  const isTimeWorkSpace =
    componentHeading === 'punch form' || componentHeading === 'time workspace';
  const { darkMode } = useDarkMode();

  return (
    <Box sx={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
      <CssBaseline />
      <AppBar position={isMobile ? 'static' : 'fixed'} open={open} isMobile={isMobile}>
        {!isProdEnv && (
          <CautionBar
            text="CAUTION: You are in the Dev environment. Changes made here do not affect production."
            bgColor="#FFA726"
          />
        )}
        <CautionBar
          text={`There are ${activeInactive.inactive} vendors waiting for approval`}
          bgColor="#23e500"
          onClick={userRole.includes('admin') ? routeChange : undefined}
        />
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            padding: '14px',
            bgcolor: 'background.paper',
            color: 'text.primary'
          }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 3,
              ...(open && { display: 'none' }),
              bgcolor: 'background.paper'
            }}>
            <ChevronRightIcon />
          </IconButton>
          {isTimeWorkSpace ? (
            <>
              <Grid container alignItems="center" flexWrap="wrap" spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography component="div" textAlign={'left'}>
                    <Typography
                      sx={{
                        fontWeight: '600 !important',
                        fontSize: { xs: '14px', md: '24px' }
                      }}>
                      Greetings, {userName}!
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#335D87',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '400'
                      }}>
                      {format(new Date(), 'EEEE dd MMMM, yyyy')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#335D87',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '400'
                      }}>
                      Labor Category:{' '}
                      <span
                        style={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          fontSize: isMobile ? '12px' : '14px'
                        }}
                        onClick={() => {
                          navigate('/admin-hub/people-workspace');
                        }}>
                        {laborCategory}
                      </span>
                    </Typography>
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={isMobile ? 'flex-start' : 'flex-end'}>
                  <Link
                    to={
                      componentHeading === 'punch form'
                        ? '/time-tracking/time-workspace'
                        : '/time-tracking/punch-form'
                    }>
                    <Button
                      variant="outlined"
                      fullWidth={isMobile}
                      startIcon={
                        componentHeading === 'punch form' ? (
                          <img src={TimeWSIcon} width={isMobile ? 15 : 20} />
                        ) : (
                          <img src={PunchFormIcon} width={isMobile ? 12 : 15} />
                        )
                      }
                      sx={{
                        color: '#335D87',
                        border: '0',
                        backgroundColor: '#335D871A',
                        borderRadius: '29px',
                        textTransform: 'none',
                        padding: isMobile ? '4px 10px' : '8px 18px',
                        fontSize: { xs: '12px', md: '16px' }
                      }}>
                      <Typography
                        sx={{ fontSize: isMobile ? '12px !important' : '16px !important' }}>
                        {componentHeading === 'punch form'
                          ? 'Navigate to Time Workspace'
                          : 'Navigate to Punch Form'}
                      </Typography>
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontWeight: '600',
                  fontSize: isMobile ? '16px !important' : '24px !important'
                }}
                component="div">
                {componentHeading}
              </Typography>
              {(componentHeading === 'Receiving Workspace' ||
                componentHeading === 'Manual PO Workspace' ||
                componentHeading === 'OCR PO Workspace') && (
                <Typography component="div" sx={{ float: 'right' }}>
                  <Link to={'/payment-workspace'}>
                    <Button
                      variant="outlined"
                      startIcon={<AddCardIcon style={{ fontSize: '1.5rem' }} />}
                      sx={{
                        color: '#335D87',
                        border: '0',
                        backgroundColor: '#335D871A',
                        borderRadius: '29px',
                        textTransform: 'math-auto'
                      }}>
                      <Typography sx={{ fontSize: '16px !important' }}>
                        {isMobile ? 'Payment ws' : 'Navigate to payment workspace'}
                      </Typography>
                    </Button>
                  </Link>
                </Typography>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? 'persistent' : 'permanent'}
        sx={{
          '& .MuiPaper-root': {
            bgcolor: 'background.paper',
            color: 'text.primary',
            visibility: 'visible !important',
            height: { xs: 'auto', md: '100vh' }
          },
          position: 'relative',
          height: { xs: '0px', sm: '100vh' }
        }}
        open={open}
        isMobile={isMobile}>
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            height: { xs: '100vh', md: 'calc(100vh - 300px)' }
          }}
          {...(isMobile ? {} : { role: 'presentation' })}>
          <DrawerHeader>
            <img
              onClick={() => navigate('/home')}
              style={{ cursor: 'pointer' }}
              width={open ? '200px' : '45px'}
              src={open ? logo : CollapsedLogo}
            />
            <IconButton
              onClick={handleDrawerClose}
              sx={{
                ...(!open && { display: 'none' }),
                bgcolor: 'background.paper',
                marginLeft: '5px'
              }}>
              <ChevronLeftIcon />
            </IconButton>
          </DrawerHeader>
          <ListItemNavButton
            {...(open
              ? {
                  sx: {
                    backgroundColor: theme.palette.primary.main,
                    ':hover': {
                      backgroundColor: theme.palette.primary.main
                    },
                    padding: '4px 6px'
                  }
                }
              : {
                  sx: {
                    border: 'none',
                    padding: '1px'
                  }
                })}>
            <img
              src={ProfileIcon}
              height={'45px'}
              style={{ borderRadius: '50%', marginRight: open ? '10px' : '1px' }}
            />
            {open && (
              <Typography component="div" sx={{ color: '#fff' }}>
                <Typography component="div" sx={{ fontSize: '12px' }}>
                  {userRole}
                </Typography>
                <Typography component="div" sx={{ fontSize: '14px' }}>
                  {userName}
                </Typography>
              </Typography>
            )}
          </ListItemNavButton>
          <Divider />

          <Typography component="div">
            <List>
              {menus.map((menu, index) =>
                menu.isNested ? (
                  <NestedNavItem
                    menu={menu}
                    key={index}
                    open={open}
                    onCloseDrawer={isMobile ? handleDrawerClose : undefined}
                  />
                ) : (
                  <SingleNavItem
                    key={index}
                    menu={menu}
                    open={open}
                    {...(isMobile ? { onCloseDrawer: handleDrawerClose } : {})}
                  />
                )
              )}
            </List>
          </Typography>
          <Divider />

          <Preference open={open} setChatModal={setChatModal} />
        </Box>
      </Drawer>

      <Main
        open={open}
        sx={{
          marginTop: isMobile ? '0px' : isTimeWorkSpace ? '200px' : '150px'
        }}
        isMobile={isMobile}>
        {/* <PaperContainer></PaperContainer> */}
        {children}
      </Main>
      {chatModal && <ChatModal onClose={() => setChatModal(false)} />}
    </Box>
  );
};

export default DashboardLayout;
